import { createSuspenseQuery } from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";
import { ReminderSchema } from "../../schemas/reminder.js";

const EntityTypeSchema = z.enum(["client", "resource", "task"]);
export type ReminderEntityType = z.infer<typeof EntityTypeSchema>;

const BaseRequestSchema = z.object({
  id: z.int().positive(),
  api: z.enum(["wf", "public"]),
  entity_type: EntityTypeSchema
});

const RequestSchema = z.discriminatedUnion("api", [
  BaseRequestSchema.extend({
    api: z.literal("wf")
  }),
  BaseRequestSchema.extend({
    api: z.literal("public"),
    slug: z.string()
  })
]);

const RequestVariables = z.union([
  BaseRequestSchema.extend({ api: z.literal("wf") }).omit({ api: true }),
  BaseRequestSchema.extend({ api: z.literal("public"), slug: z.string() }).omit(
    { api: true }
  )
]);

const ResponseSchema = z.array(ReminderSchema);

type Response = z.infer<typeof ResponseSchema>;
type Request = z.infer<typeof RequestSchema>;

export type EntityRemindersResponse = Response;
export type EntityRemindersRequest = Request;

export type EntityRemindersProps = Request;

const urls = {
  wf: {
    task: "/task/:id/reminders",
    resource: "/resource/:id/reminders",
    client: "/client/:id/reminders"
  },
  public: {
    task: "/public/:slug/task/:id/reminders",
    resource: "/public/:slug/resource/:id/reminders",
    client: "/public/:slug/client/:id/reminders"
  }
} as const;

export const useEntityRemindersSuspense = (entity_type: ReminderEntityType) =>
  createSuspenseQuery<Response, Request, Error>({
    queryKey: [`entity${entity_type}RemindersWithSuspense`],
    staleTime: 1000 * 60,
    fetcher: async (body) => {
      const { api: apiType, ...rest } = body;
      const result = await api<Omit<Request, "api">, Response>({
        method: "GET",
        url: urls[apiType][rest.entity_type],
        requestSchema: RequestVariables,
        responseSchema: ResponseSchema,
        body: rest
      });
      return result;
    }
  });
